// Writes an OPFS file. Safari before 26 has no createWritable: there a file
// can only be written from a worker, with a sync access handle (Safari 15.2+).
// The wasm app does the same (WasmShell.js in sumatrapdf).

const WRITER_SOURCE = `
onmessage = async (e) => {
  const m = e.data;
  try {
    let dir = await navigator.storage.getDirectory();
    for (const part of m.dirs) dir = await dir.getDirectoryHandle(part, { create: true });
    const fh = await dir.getFileHandle(m.name, { create: true });
    const h = await fh.createSyncAccessHandle();
    try {
      h.truncate(0);
      h.write(m.bytes, { at: 0 });
      h.flush();
    } finally {
      h.close();
    }
    postMessage({ id: m.id });
  } catch (err) {
    postMessage({ id: m.id, error: String((err && err.message) || err) });
  }
};
`;

type Call = { resolve: () => void; reject: (e: Error) => void };

let writer: Worker | null = null;
const calls = new Map<number, Call>();
let nextId = 1;

function writerWorker(): Worker {
  if (writer) {
    return writer;
  }
  const url = URL.createObjectURL(new Blob([WRITER_SOURCE], { type: "text/javascript" }));
  writer = new Worker(url);
  writer.onmessage = (e: MessageEvent<{ id: number; error?: string }>) => {
    const call = calls.get(e.data.id);
    calls.delete(e.data.id);
    if (!call) {
      return;
    }
    if (e.data.error) {
      call.reject(new Error(e.data.error));
    } else {
      call.resolve();
    }
  };
  return writer;
}

async function writeInWorker(handle: FileSystemFileHandle, bytes: Uint8Array<ArrayBuffer>): Promise<void> {
  const root = await navigator.storage.getDirectory();
  const parts = await root.resolve(handle);
  if (!parts || parts.length === 0) {
    throw new Error(`${handle.name} is not in OPFS`);
  }
  const id = nextId++;
  const done = new Promise<void>((resolve, reject) => calls.set(id, { resolve, reject }));
  writerWorker().postMessage({ id, dirs: parts.slice(0, -1), name: parts[parts.length - 1], bytes });
  return done;
}

async function toBytes(data: BlobPart): Promise<Uint8Array<ArrayBuffer>> {
  if (data instanceof ArrayBuffer) {
    return new Uint8Array(data);
  }
  return new Uint8Array(await new Blob([data]).arrayBuffer());
}

export async function writeOpfsFile(handle: FileSystemFileHandle, data: BlobPart): Promise<void> {
  const bytes = await toBytes(data);
  if (typeof handle.createWritable === "function") {
    const writable = await handle.createWritable();
    try {
      await writable.write(bytes);
    } finally {
      await writable.close();
    }
  } else {
    await writeInWorker(handle, bytes);
  }
  // a browser can take the write and keep nothing (WebKit's Windows port does)
  const kept = (await handle.getFile()).size;
  if (kept !== bytes.length) {
    throw new Error(`the browser kept ${kept} of ${bytes.length} bytes of ${handle.name}`);
  }
}
